import { Component, computed, Injector, input, OnInit, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FeatureFlags } from '../../../../../../../global/featureFlags/featureFlags';
import { TextInputComponent } from '../../../../../../../shared/components/forms/text-input/text-input.component';
import { TextSearchComponent } from '../../../../../../../shared/components/forms/text-search/text-search.component';
import { AnimateOpenDirective } from '../../../../../../../shared/directives/animate-open.directive';
import { templateDrivenFormsViewProviders } from '../../../../../../../shared/directives/form.directive';
import { StringifyPipe } from '../../../../../../../shared/pipes/stringify';
import { stripProductTier } from '../../../../../../../shared/utils/formatting';
import { getBrandImagesForManufacturer } from '../../../../../../../shared/utils/get-model-number-image';
import { GAService } from '../../../../services/ga-tags.service';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { getErrorsFromSubmission } from '../../pipes/error-subscription';
import { formValidationGenerator } from '../../pipes/form-validation';
import { genericModelNumbers } from './generics';

@Component({
  selector: 'app-appliance-details',
  imports: [
    TextSearchComponent,
    AnimateOpenDirective,
    FormsModule,
    ReactiveFormsModule,
    StringifyPipe,
    TextInputComponent,
  ],
  templateUrl: './appliance-details.component.html',
  styleUrl: './appliance-details.component.scss',
  viewProviders: [templateDrivenFormsViewProviders],
})
export class ApplianceDetailsComponent implements OnInit {
  formSubmitted = input<boolean>(false);

  searchTerm = '';

  formValue = input<Record<string, any>>({});

  panelExpanded = signal(false);

  apiLookupError = signal(false);

  formValid = output<boolean>();

  inputConfig: Record<string, any> = {};

  errors = signal<Record<string, Record<string, any>>>({});

  genericModelNumberFeatureFlagActive = FeatureFlags.getFeatureFlag('genericModelNumbers');

  confirmBlankModelNumber = input(false);

  private readonly productTypeCopy: Record<string, { applianceDescription: string; whereToFindItems: string[] }> = {
    isTV: {
      applianceDescription: 'TV',
      whereToFindItems: [
        'On a label on the back of your TV',
        "In your TV's user manual",
        "In your TV's menu settings (look for 'model' or 'mod')",
      ],
    },
    isGadget: {
      applianceDescription: 'gadget',
      whereToFindItems: ['In your product’s user manual', 'On a label on the back of your product'],
    },
    default: {
      applianceDescription: '',
      whereToFindItems: [
        'In your product’s user manual',
        "On a label on the side of your appliance or inside the door (look for a number called 'model' or 'mod')",
      ],
    },
  };

  private viewModel = computed(() => {
    const { applianceCode, manufacturer, isTV, isGadget, isFloorCareProduct } =
      this.orbitFormClaimService.state.claimData.bundle;
    const productType = stripProductTier(this.orbitFormClaimService.state.claimData.bundle.productType);
    const mandatoryData = this.orbitFormClaimService?.state?.claimData?.mandatoryData;
    const { applianceSuperCategory } = this.orbitFormClaimService.state.claimData.superCategory ?? {};
    const modelApplianceCombination = `${manufacturer?.toLowerCase()}_${applianceCode}`;
    const productDescription = productType?.toLowerCase();

    const isOptionalModelNumber = mandatoryData?.oEMModelDataRequired === false;
    const hasBVLookupData = !!mandatoryData.bevaluedCategoryID;

    const useBVModelLookup = isOptionalModelNumber && hasBVLookupData;
    return {
      searchTerm: this.searchTerm,
      claimData: this.orbitFormClaimService.state.claimData,
      panelExpanded: this.panelExpanded(),
      productDescription,
      loadingData: false,
      formSubmitted: this.formSubmitted(),
      submittingError: this.orbitFormClaimService.formSubmission.error,
      brandImage: getBrandImagesForManufacturer(applianceCode, applianceSuperCategory, manufacturer),
      formValue: this.formValue(),
      showGenericModelNumberBox:
        this.genericModelNumberFeatureFlagActive &&
        ((mandatoryData?.oEMModelDataRequired && genericModelNumbers[modelApplianceCombination]) ||
          (isOptionalModelNumber && hasBVLookupData)),
      genericModelCode: genericModelNumbers[modelApplianceCombination]?.modelCode,
      genericModelData: genericModelNumbers[modelApplianceCombination],
      errors: this.errors(),
      bvModelNumberLookupData: {
        group: mandatoryData?.bevaluedCategoryID as string,
        manufacturer,
      },
      submittingResource: this.orbitFormClaimService.formSubmission,
      useBVModelLookup,
      modelNumberRequired: mandatoryData?.oEMModelDataRequired || useBVModelLookup,
      useBVModelNumberLookup: isOptionalModelNumber && hasBVLookupData,
      isTelevision: !!isTV,
      whyDoWeNeedThisCheckList: isGadget
        ? ['It helps us identify what’s wrong, so we can fix it', 'Order the right parts for the repair']
        : [
            'It helps us identify what’s wrong, so we can fix it',
            'It allows us to bring the right parts for your product',
          ],

      numberIsImportantPoints: isGadget
        ? ['Understand exactly what’s wrong with your product', 'Order the right parts for the repair']
        : ['Understand exactly what’s wrong with your appliance', 'Bring the right parts on the first visit'],
      numberIsImportantFinalLine:
        isGadget || isFloorCareProduct
          ? 'Without the number, your product <strong>will probably take longer to repair.</strong>'
          : 'Without the number, your repair will probably take <strong>more than one visit.</strong>',
      ...this.resolveProductTypeCopy(this.orbitFormClaimService.state.claimData.bundle, productDescription),
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly injector: Injector,
    private readonly gaService: GAService
  ) {}

  ngOnInit(): void {
    this.apiLookupError.set(this.orbitFormClaimService.modelNumberLookupError());
    this.getConfigForEachStep();
    this.handleFormValidation();
    this.handleAPIErrors();
    if (this.vm.formValue?.['cannot-find-number']) {
      // Timeout used to give the page time to load in content inside the expandable element
      setTimeout(() => {
        this.panelExpanded.set(true);
      }, 100);
    }

    this.trackPageLoad();
  }

  handleAPIErrors(): void {
    getErrorsFromSubmission(this.vm.submittingResource, this.injector, (attempts, error) => {
      console.log({ error });
    });
  }

  getConfigForEachStep(): void {
    const configMap = this.orbitFormClaimService.state.claimData.mandatoryData.oEMApplianceData
      .filter((item) => {
        return item.dataType === 'ModelNumber' || item.dataType === 'PNCNumber';
      })
      .reduce((acc: any, item: any) => {
        acc[item.dataType] = item;

        return acc;
      }, {});

    this.inputConfig = configMap;
  }

  setModelNumberError() {
    this.orbitFormClaimService.modelNumberLookupError.set(true);
  }

  handleFormValidation(): void {
    toObservable(this.formValue, { injector: this.injector })
      .pipe(
        formValidationGenerator({}, [
          (value) => {
            if (this.vm.useBVModelLookup && this.apiLookupError()) {
              return null;
            }

            if (!value['modelNumber'] && !value['cannot-find-number'] && this.vm.modelNumberRequired) {
              return {
                required: true,
              };
            }
            return null;
          },
        ])
      )
      .subscribe({
        next: ({ isFormValid, errors }) => {
          if (this.vm.formValue?.['cannot-find-number'] && this.vm.modelNumberRequired) {
            this.orbitFormClaimService.updateGenericModelNumberData({
              ...this.vm.genericModelData,
              modelApplianceCombination: '',
            });
            this.orbitFormClaimService.updateModelNumberState(true);
          } else {
            this.orbitFormClaimService.updateModelNumberState(false);
          }

          this.errors.set(errors);
          this.formValid.emit(isFormValid);
        },
      });
  }

  private resolveProductTypeCopy(
    bundle: typeof this.orbitFormClaimService.state.claimData.bundle,
    productDescription: string | undefined
  ): { applianceDescription: string; whereToFindItems: string[] } {
    if (bundle.isTV) return this.productTypeCopy['isTV'];
    if (bundle.isGadget || bundle.isFloorCareProduct)
      return { ...this.productTypeCopy['isGadget'], applianceDescription: productDescription ?? '' };
    return { ...this.productTypeCopy['default'], applianceDescription: productDescription ?? '' };
  }

  trackClick() {
    this.gaService.sendGATag({
      ...this.gaService.GA_MODEL_NUMBER.WHERE_TO_FIND_NUMBER_CLICKED(),
      productModel: this.vm.formValue?.['modelNumber'] || '',
    });
  }

  onCheckboxKeydown(event: KeyboardEvent, currentValue: boolean) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      const newValue = !currentValue;
      const input = event.target as HTMLInputElement;

      input.checked = newValue;

      const changeEvent = new Event('change', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  }

  trackPageLoad() {
    this.gaService.sendGATag(this.gaService.GA_MODEL_NUMBER.PAGE_LOAD());
  }

  setStaticValue(modelNumber: string) {
    this.vm.formValue['modelNumber'] = modelNumber;
    this.vm.formValue['cannot-find-number'] = false;
  }
}
